/**
 * `planPrefill` — the PURE planner for conservative prefill at sponsored client setup
 * (steward-app#1016, "Conversion re-scoped" comment 2026-09-16; design memo §3/4/6, PR B1
 * of the split in that memo's "Proposed PR split").
 *
 * When a firm converts a verified intake into a sponsored Steward workspace, the family's
 * typed answers become ordinary hand-entered rows — never rows that could pass as synced
 * or connection-owned. This module does no I/O: it takes the intake, an optional Plaid
 * account-link snapshot, and a caller-supplied id/date, and returns a frozen, ordered list
 * of row intents. A later background Lambda (PR B3, not this one) writes them through
 * appcore's `/system/*` plane with create-if-absent, using the ids this file derives.
 *
 * ## The one invariant every row in this file must hold
 *
 * A prefilled row must never be indistinguishable from a synced one. Concretely:
 *   - `account`, `asset` and `liability` attributes never set `source` to anything but
 *     `undefined` (never `'manual'` either, though that would also be safe — omitting it
 *     is simplest and `isSynced` treats the two identically, see `src/api/types.ts:484`);
 *   - none of the three ever sets `externalId` or `connectionId`;
 *   - a `valuation`'s `source` is always free text (`PREFILL_VALUATION_SOURCE` or
 *     `PREFILL_SNAPSHOT_VALUATION_SOURCE`), never one of `valuationSource.ts`'s
 *     `MACHINE_VALUATION_SOURCES` (`'plaid'`, `'rentcast'`) — `isMachineSourceCode` must
 *     classify both as person-editable.
 *   - `source: 'intake'` is never used anywhere. The design memo calls this out by name
 *     (§4): it would look like a fourth sync provider and make every prefilled row
 *     read-only and undeletable, which is the opposite of "hand-entered".
 *
 * `intakePrefill.test.ts` pins this for every row kind in a maximal fixture. The source
 * rule and the contact-field exclusion below were also mutation-tested by hand during this
 * PR's review (a source edit that leaked `relationshipQuality`, and one that wrote a
 * machine `source` on a snapshot valuation, were both confirmed to fail the suite before
 * being reverted) — that was a one-time check on this PR, not something the suite re-runs,
 * so it is recorded here and in the PR body rather than claimed as an ongoing guarantee.
 *
 * ## What is never copied, and why
 *
 * - `person` rows never carry a date of birth or address from the INTAKE (the intake asks
 *   no adult date of birth, and a child's is free text there), and never `gender`/`employer`
 *   (`person` has no field for either). Dates of birth (children's included) and the legal and
 *   mailing address come from the firm's ONBOARDING DETAILS instead (`options.onboarding`,
 *   crossmarc/steward-app#1767), and a child's intake date of birth is used only when it is a
 *   real `YYYY-MM-DD` date.
 * - `person` rows carry `phone` and `email` (#1957) in exactly two cases, both from ONBOARDING:
 *   the spouse's own `spouse_phone` / `spouse_email` answers, when present; and an adult child the client chose as
 *   their trusted contact, who gets the phone and email the resolver returns. The self row never
 *   carries either: the account email is a login identifier and stays off the person. A minor, a
 *   stale child choice, or any child who was not chosen gets neither.
 * - The onboarding trusted contact (`resolveTrustedContact`, #1954) becomes one `contact` row
 *   (category `other`) when it is someone else, and the self row points at it through
 *   `trustedContactId`. When the client chose their spouse or an adult child, NO contact row is
 *   written: the self row points at that family member's `person` row instead, planned before
 *   the self row. The onboarding CPA and attorney are
 *   never read here, nor the beneficiary or About answers: those stay the firm's.
 * - `contact` rows (CPA, attorney, other advisors) never carry `relationship` or
 *   `relationshipQuality`. Neither field is even declared on `contact` in
 *   `STEWARD_APP_TYPE_DEFS` (`index.ts`), so this is enforced twice over: there is no
 *   attribute name to put it under, and this file never tries.
 * - `incomeAndGoals`, `insuranceAndEstate` and `investmentProfile` produce nothing. They
 *   stay the firm's own record (design memo §3's mapping table).
 * - Snapshot `holdings` and intake `statements` are not emitted in v1 (memo §3).
 *
 * ## Assumptions pending a founder ruling
 *
 * Named as single constants so a ruling is a one-line change, per the brief:
 *   - {@link PREFILL_CURRENCY} — the intake asks no currency question at all
 *     (`intakeSchema.ts` has no field for it); USD is assumed.
 *   - {@link PREFILL_LUMP_INVESTMENT_ASSET_CLASS} — a 401(k)/IRA/brokerage balance is an
 *     unknown mix of holdings, so `'other'` is the only honest choice among
 *     `assetClasses.ts`'s named classes; it is not a claim that the account holds
 *     "other" assets specifically.
 *   - {@link PREFILL_PRIVATE_PLACEMENT_ASSET_CLASS} — the intake's `private-placements`
 *     other-asset type has no obviously matching class; `'private equity'` is the closest
 *     named group in `assetClasses.ts`.
 *
 * ## The snapshot/typed-account rule (coordinator revision, 2026-09-16)
 *
 * See {@link snapshotSuppressesTypedAccounts}. An earlier draft matched by institution
 * NAME, case-insensitively, and an independent review rejected it as too fuzzy: what a
 * family types ("Chase") and what Plaid reports ("JPMorgan Chase") name the same bank and
 * would never match, so a name-based rule would under-suppress the exact duplicates it
 * exists to catch far more often than it would wrongly suppress a distinct account. The
 * rule is now all-or-nothing: ANY snapshot account at all suppresses EVERY typed cash and
 * investment row. Blunter, but never wrong in the dangerous direction — a family who typed
 * one bank and separately linked a different one loses the typed row (a real, visible
 * loss the advisor's "what was copied" summary in PR B4 should call out), but two
 * balances for the same money can never silently double-count net worth.
 */

import { ADVISOR_FIRM_KINDS } from './advisorFirmDirectory.js';
import {
  intakeLabel, RENTAL_OCCUPANCY_LABELS, PROPERTY_PLAN_LABELS, BUSINESS_RENTS_SPACE_LABELS,
  VACATION_RENTAL_INCOME_LABELS, ENTITY_TYPE_LABELS, REVENUE_RANGE_LABELS, EMPLOYEE_COUNT_LABELS,
  OWNER_PAY_LABELS, OTHER_OWNERS_LABELS, BUSINESS_HOLDINGS_LABELS, EXIT_THINKING_LABELS, BUY_SELL_LABELS,
} from './intakeLabels.js';
import { isEmptyValue, propertyAsks, asksOtherOwnersDetails, asksBusinessHoldingsDetails, type IntakeSubmission } from './intakeSchema.js';
import { SNAPSHOT_AMOUNT_MAGNITUDE_BOUND } from './intakeSnapshot.js';
import type { IntakeDraftSnapshot, IntakeSnapshotAccount } from './intakeSnapshot.js';
import { resolveTrustedContact } from './onboardingTrustedContact.js';
import { latestValuationRow, utcToday } from './latestValuation.js';
import { valuationInstanceId } from './valuationInstanceId.js';

/**
 * Fallback for a required `account.institution` this file cannot fill in. Deliberately
 * NOT imported from `index.ts`'s `UNNAMED_INSTITUTION`: that barrel file is where THIS
 * module gets re-exported from (`export * from './intakePrefill.js'`), so importing
 * back from it would be a circular module dependency. Byte-identical to that constant on
 * purpose — `intakePrefill.test.ts` pins the two staying equal — because it means the
 * same thing: a required "who is this with" field with nothing honest to put in it.
 */
const UNNAMED_INSTITUTION_FALLBACK = 'Connected account';

// ── Assumptions pending a founder ruling (see header) ───────────────────────────────────

/** USD. The intake asks no currency question; see the module header. */
export const PREFILL_CURRENCY = 'USD';

/**
 * The asset class for a lump investment-account balance (401(k), IRA, brokerage, …) — see
 * the module header. Must be one of `assetClasses.ts`'s `ASSET_CLASSES` codes;
 * `intakePrefill.test.ts` asserts that directly so a future rename of the registry does
 * not silently orphan this string.
 */
export const PREFILL_LUMP_INVESTMENT_ASSET_CLASS = 'other';

/** The asset class for an intake `private-placements` other-asset row — see the module
 *  header. Also checked against `ASSET_CLASSES` by the test. */
export const PREFILL_PRIVATE_PLACEMENT_ASSET_CLASS = 'private equity';

// ── Never-synced markers ─────────────────────────────────────────────────────────────────

/**
 * Free-text `valuation.source` for a hand-typed intake figure. `isMachineSourceCode`
 * (`valuationSource.ts`) only recognises `'plaid'` and `'rentcast'`, so any other string
 * — including this one — leaves the row editable and deletable like any manual valuation.
 */
export const PREFILL_VALUATION_SOURCE = 'From your intake';

/** Same rule, worded for a value the prospect echoed from a Plaid link rather than typed
 *  directly — see the module header's snapshot section and design memo §3. */
export const PREFILL_SNAPSHOT_VALUATION_SOURCE = 'Shared at intake';

// ── Person relationship labels (free text on `person.relationship`, not an enum) ────────

export const RELATIONSHIP_SELF = 'Self';
const RELATIONSHIP_SPOUSE = 'Spouse or partner';
const RELATIONSHIP_CHILD = 'Child';

// ── Account/liability/contact enum values ────────────────────────────────────────────────
//
// `account.accountType` and `liability.liabilityType` are `type: 'enum'` in
// `STEWARD_APP_TYPE_DEFS` with no `options` list — the actual value sets are NOT declared
// anywhere in steward-types, and both the SPA (`src/api/types.ts` `ACCOUNT_TYPES` /
// `LIABILITY_TYPES`) and the façade (`services/steward-app-api/src/handlers/liabilities.ts`
// `z.enum([...])`) already hand-copy them independently — checked 2026-09-16, and this file
// adds a THIRD copy rather than a fourth import, since steward-types sits below the SPA and
// the façade in the dependency graph and cannot import either. What this file CAN do is
// name its own output vocabulary once ({@link PREFILL_ACCOUNT_TYPES},
// {@link PREFILL_LIABILITY_TYPES}) and let `src/intake/intakePrefillEnums.test.ts` — the
// one layer that can see both this package and the SPA — assert every value in it is one
// the SPA's own list recognises. A rename on the SPA side reds that test instead of
// silently rendering an option the portal's own `<select>` does not know.
//
// `contact.category` is DIFFERENT: steward-types already declares this vocabulary, as
// {@link ADVISOR_FIRM_KINDS} (`advisorFirmDirectory.ts`) — "`ContactCategory` minus
// `doctor`", kept in lockstep with the SPA's `ContactCategory` by the existing
// `src/advisor/advisorFirmKinds.test.ts`. No new parity test is needed for it; this file
// only has to stay inside `[...ADVISOR_FIRM_KINDS, 'doctor']`, which
// `intakePrefill.test.ts` checks directly.

/** Every `account.accountType` value this module can ever emit. See the section header. */
export const PREFILL_ACCOUNT_TYPES = ['Checking', 'Savings', 'Other', 'Retirement', 'Brokerage'] as const;

/** Every `liability.liabilityType` value this module can ever emit. See the section header. */
export const PREFILL_LIABILITY_TYPES = ['mortgage', 'loan', 'line-of-credit', 'other'] as const;

/** `contact.category` minus `doctor` — see the section header. `doctor` is not reachable
 *  from an intake's CPA/attorney/other-advisor rows in practice, but is kept as a possible
 *  {@link guessAdvisorCategory} outcome since it IS a real `ContactCategory`. */
const CONTACT_CATEGORIES_REACHABLE = [...ADVISOR_FIRM_KINDS, 'doctor'] as const;

/** Cash account type, by the intake's own `INTAKE_CASH_ACCOUNT_TYPES` code. */
function cashAccountType(code: string): typeof PREFILL_ACCOUNT_TYPES[number] {
  if (code === 'checking') return 'Checking';
  if (code === 'savings') return 'Savings';
  return 'Other'; // money-market, cd, high-yield-savings
}

/** Investment account type, by the intake's own `INTAKE_INVESTMENT_ACCOUNT_TYPES` code. */
function investmentAccountType(code: string): typeof PREFILL_ACCOUNT_TYPES[number] {
  if (code === '401k' || code === 'ira' || code === 'roth-ira' || code === 'sep-ira') return 'Retirement';
  if (code === 'brokerage') return 'Brokerage';
  return 'Other'; // annuity, other
}

/** Liability type, by the intake's own `INTAKE_DEBT_TYPES` code. */
function debtLiabilityType(code: string): typeof PREFILL_LIABILITY_TYPES[number] {
  if (code === 'loan') return 'loan';
  if (code === 'line-of-credit') return 'line-of-credit';
  return 'other'; // credit-card, other
}

const OTHER_ASSET_CLASS: Record<string, string> = {
  crypto: 'crypto',
  collectibles: 'art & collectibles',
  vehicles: 'vehicle',
  'private-placements': PREFILL_PRIVATE_PLACEMENT_ASSET_CLASS,
  other: 'other',
};

const CPA_ATTORNEY_CATEGORY = { cpa: 'accountant', attorney: 'lawyer' } as const;

/**
 * Best-effort category for a free-text "other advisor" role (e.g. "insurance agent",
 * "estate planner"). Not one of the brief's named pending-ruling constants — it is a
 * display convenience, not a figure — but documented as a guess: an unmatched role
 * defaults to `'other'` rather than a guess that could misfile someone. The return type
 * pins every branch to a real `ContactCategory` (see {@link CONTACT_CATEGORIES_REACHABLE}).
 */
function guessAdvisorCategory(role: string | undefined): typeof CONTACT_CATEGORIES_REACHABLE[number] {
  const text = (role ?? '').toLowerCase();
  if (text.includes('insurance')) return 'insurance';
  if (text.includes('estate')) return 'estate';
  if (/\b(account|cpa|tax)/.test(text)) return 'accountant';
  if (/\b(attorney|lawyer|legal)/.test(text)) return 'lawyer';
  if (/\b(doctor|physician|medical)/.test(text)) return 'doctor';
  if (/\b(financial|advisor|adviser|planner|wealth)/.test(text)) return 'financial-advisor';
  return 'other';
}

// ── Name fallbacks (used only when the intake left the row's own name blank) ────────────

const PROPERTY_TYPE_NAME: Record<string, string> = {
  'primary-residence': 'Primary residence',
  rental: 'Rental property',
  commercial: 'Commercial property',
  vacation: 'Vacation property',
  land: 'Land',
};

const OTHER_ASSET_TYPE_NAME: Record<string, string> = {
  crypto: 'Crypto',
  collectibles: 'Collectibles',
  vehicles: 'Vehicle',
  'private-placements': 'Private placement',
  other: 'Other asset',
};

const DEBT_TYPE_NAME: Record<string, string> = {
  loan: 'Loan',
  'line-of-credit': 'Line of credit',
  'credit-card': 'Credit card',
  other: 'Other debt',
};

const CASH_TYPE_LABEL: Record<string, string> = {
  checking: 'Checking',
  savings: 'Savings',
  'money-market': 'Money market',
  cd: 'CD',
  'high-yield-savings': 'High-yield savings',
};

const INVESTMENT_TYPE_LABEL: Record<string, string> = {
  '401k': '401(k)',
  ira: 'IRA',
  'roth-ira': 'Roth IRA',
  'sep-ira': 'SEP IRA',
  brokerage: 'Brokerage account',
  annuity: 'Annuity',
  other: 'Investment account',
};

// ── Money parsing ─────────────────────────────────────────────────────────────────────────

/**
 * Digits, an optional leading `$`, optional thousands commas in proper groups of three,
 * and an optional decimal part. Deliberately rejects everything the RIPL-style intake
 * text allows and a ledger figure cannot: a range ("100-200"), a word ("approx", "about",
 * "n/a"), a sign ("-100"), and a `k`/`m` suffix ("20k", "2M"). An unparseable value is
 * left out of the plan entirely rather than guessed at.
 *
 * ⚠️ The SHAPE check alone is not a magnitude bound (steward-app#1490, found by the
 * independent review of this PR). `intakeSchema.ts`'s `amount` field is bounded only by
 * LENGTH (100 characters, `intakeSchema.ts:76`), so `'9'.repeat(100)` is a shape this
 * pattern happily matches and `Number(...)` happily parses — to `1e100`, which
 * `Number.isFinite` does not catch (finite just means not `Infinity`/`NaN`; `1e100` is a
 * finite, valid, and absurd `number`). That is the #738 class: `advisor.ts` ~3494 records
 * a similar figure overflowing `Number.MAX_VALUE` to `Infinity`, which then serialises to
 * `null` and renders as $0 — an unbounded parse here would have written the same kind of
 * poison directly into `marketValue`/`currentBalance`. `parseStrictMoney` now rejects
 * anything past {@link SNAPSHOT_AMOUNT_MAGNITUDE_BOUND}, reusing the bound
 * `intakeSnapshot.ts`'s own money figures already carry rather than inventing a second one.
 */
const STRICT_MONEY_PATTERN = /^\$?(?:\d{1,3}(?:,\d{3})*|\d+)(?:\.\d+)?$/;

/** Parses a strict money string to a number, or `null` if it does not match OR its
 *  magnitude exceeds {@link SNAPSHOT_AMOUNT_MAGNITUDE_BOUND} (see the pattern's own
 *  docblock). `undefined` and blank/whitespace-only input also yield `null`. */
export function parseStrictMoney(raw: string | undefined): number | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed === '' || !STRICT_MONEY_PATTERN.test(trimmed)) return null;
  const value = Number(trimmed.replace(/^\$/, '').replace(/,/g, ''));
  if (!Number.isFinite(value) || Math.abs(value) > SNAPSHOT_AMOUNT_MAGNITUDE_BOUND) return null;
  return value;
}

/**
 * Same strictness, for a percentage-shaped field (`liability.interestRate`). A trailing
 * `%` is accepted and stripped; nothing else is. `"3.25% or n/a"` (a real RIPL-style
 * answer) does not parse, and the rate is simply left off the liability.
 *
 * Bounded to `[0, 100]` (steward-app#1490, same magnitude-overflow class as
 * {@link parseStrictMoney}): the shape pattern's `\d{1,3}` already blocks a value with
 * more than 3 whole digits, so a hundred-character all-nines string cannot reach `1e100`
 * here the way it could through the money pattern's unbounded `\d+` — but a value that
 * genuinely fits the shape ("999.9999") could still be a nonsense interest rate without
 * an explicit range check, and appcore's `interestRate` field carries no bound of its own.
 */
const STRICT_PERCENT_PATTERN = /^\d{1,3}(?:\.\d+)?%?$/;

export function parseStrictPercent(raw: string | undefined): number | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed === '' || !STRICT_PERCENT_PATTERN.test(trimmed)) return null;
  const value = Number(trimmed.replace(/%$/, ''));
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return value;
}

// ── Deterministic ids (design memo §6) ───────────────────────────────────────────────────
//
// steward-types carries NO hash primitive of its own (coordinator revision, 2026-09-16):
// an earlier draft hand-rolled a dependency-free SHA-256 here, because `vite.config.ts`
// aliases `@steward-app/steward-types` straight to this package's OWN SOURCE, so the SPA
// bundles this module directly and `node:crypto` is not an option. A hand-written crypto
// primitive is a maintenance and trust liability even when cross-checked, and nothing in
// THIS module needs it — only the id needs to be deterministic, and that requirement is
// satisfiable by whoever CALLS `planPrefill` supplying the function. So `options.rowId`
// is injected: the two real callers (the façade and the background Lambda, both Node)
// inject the same `node:crypto`-backed function, and this module's own Node-free
// guarantee holds regardless of what they choose.
//
// What this module keeps is the FORMAT rule the design memo's scheme promises appcore:
// `pf` + 30 lowercase hex characters, 32 total, comfortably inside appcore's
// `[A-Za-z0-9_.-]{1,64}` id grammar. `rowId` is validated against it and `planPrefill`
// THROWS on a bad id — a malformed id fails loudly here rather than reaching appcore's
// create call and failing as an opaque 400.

const ROW_ID_PATTERN = /^pf[0-9a-f]{30}$/;

/**
 * Turns one row's seed — `${setupId}|${kind}|${index}` (design memo §6; `kind` is a short,
 * stable label for what the row IS, e.g. `'cash-account'`, and `index` is that KIND's own
 * position, not a running counter over the whole plan, so a row of one kind never shifts
 * the ids of a different kind emitted around it) — into a validated instance id by calling
 * the caller-injected `rowId` function. Throws if the result is not `pf` + 30 lowercase hex
 * characters.
 */
function deriveRowId(rowIdFn: (seed: string) => string, setupId: string, kind: string, index: number): string {
  const seed = `${setupId}|${kind}|${index}`;
  const id = rowIdFn(seed);
  if (!ROW_ID_PATTERN.test(id)) {
    throw new Error(`planPrefill: rowId("${seed}") returned "${id}", which is not "pf" + 30 lowercase hex characters`);
  }
  return id;
}

/**
 * Every valuation this module writes uses `valuationInstanceId(assetId, date)`
 * (`valuationInstanceId.ts`) rather than {@link deriveRowId}, per the design memo (§6).
 * That helper's own docblock says its same-date tie-break ordering exists so a MACHINE
 * writer wins against a family's same-day row — worth naming explicitly, since a prefilled
 * valuation is not machine-written in the sense that scheme means.
 *
 * Reviewed on the merits (code review of this PR, 2026-09-16) and kept as-is: the tie only
 * matters if TWO valuation rows exist for the same (assetId, date), and the façade's own
 * `POST /valuations` refuses that outright — `services/steward-app-api/src/handlers/
 * valuations.ts` 409s "This holding already has a value recorded for that date" before a
 * second row can ever be created, and correcting the EXISTING one goes through
 * `PUT /valuations/{id}` on the SAME instanceId, in place, not a new row. So the scenario
 * the ordering rule guards against — a second, later row silently losing a tie to this
 * one — cannot arise through the app's own write path for a manual (never synced) asset,
 * which every asset this module creates is. What `valuationInstanceId` is actually needed
 * for here is exactly what the memo asks of it: a deterministic, collision-checked
 * create-if-absent id so a redelivered prefill message never double-writes.
 */

// ── The plan itself ──────────────────────────────────────────────────────────────────────

/**
 * Every attribute key {@link planPrefill} can emit, per type, and nothing else
 * (steward-app#1491's independent review). The writer (`services/client-prefill`) refuses a
 * message carrying any other key, so this list is the positive bound on what a prefill can write:
 * a sync-owned key (`quantity`, `beneficiaries`, the provenance triple on a portfolio row), a
 * server-managed link (`person.linkedUserId`) or a protected field can reach appcore only by being
 * added HERE, where `intakePrefill.test.ts` pins the list to the planner's actual output in both
 * directions (no emitted key missing, no listed key never emitted).
 */
export const PREFILL_ATTRIBUTE_KEYS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  account: ['name', 'institution', 'accountType', 'currency', 'lastFourHint', 'intakeAccountId', 'notes'],
  asset: [
    'accountId', 'name', 'assetClass', 'currency', 'valuationMode', 'notes',
    'latestValuationValue', 'latestValuationDate', 'latestValuationId',
  ],
  valuation: ['assetId', 'date', 'marketValue', 'currency', 'source'],
  liability: ['name', 'liabilityType', 'currentBalance', 'currency', 'currentBalanceAsOf', 'linkedAssetId', 'interestRate', 'notes'],
  contact: ['name', 'category', 'organization', 'phone', 'email', 'notes'],
  person: [
    'legalName', 'firstName', 'lastName', 'relationship', 'phone', 'email', 'dateOfBirth', 'legalAddress', 'mailingAddress',
    'trustedContactId', 'onboardingSubject', 'onboardingSource',
  ],
});

/**
 * The two onboarding stamps (crossmarc/appcore#739), on the self and spouse person rows only.
 * `createOnly` on the `person` type (`index.ts`): the client prefill is the only writer, at
 * create, and appcore's tax-id copy finds its target by them. Named here so the planner, the
 * writer's allowlist above and the tests agree on one spelling.
 */
export const ONBOARDING_SUBJECT_KEY = 'onboardingSubject';
export const ONBOARDING_SOURCE_KEY = 'onboardingSource';

/**
 * The `person` fields the pack declares `createOnly` (today: the two stamps above). Derived from
 * `STEWARD_APP_TYPE_DEFS` rather than listed, so a member-plane write gate (the façade's
 * `refuseAttributes` on `person`, the SPA's `SERVER_OWNED_PERSON_KEYS`) cannot drift from the
 * pack. Takes the defs as an argument because `index.ts` defines the pack AND re-exports this
 * module, so importing the pack here would be a cycle.
 */
export function personCreateOnlyFieldNames(defs: ReadonlyArray<{ typeCode: string; fields?: unknown }>): string[] {
  const person = defs.find(d => d.typeCode === 'person');
  const fields = (person?.fields ?? []) as Array<{ name: string; createOnly?: boolean }>;
  return fields.filter(f => f.createOnly === true).map(f => f.name);
}

/**
 * The `person` fields the pack declares `routeOnly` (today: `linkedUserId`, steward-app#1851).
 * Only appcore's dedicated routes write one; the generic member plane refuses the key on
 * presence. Derived for the same reason as {@link personCreateOnlyFieldNames}.
 */
export function personRouteOnlyFieldNames(defs: ReadonlyArray<{ typeCode: string; fields?: unknown }>): string[] {
  const person = defs.find(d => d.typeCode === 'person');
  const fields = (person?.fields ?? []) as Array<{ name: string; routeOnly?: boolean }>;
  return fields.filter(f => f.routeOnly === true).map(f => f.name);
}

/** appcore's onboarding id grammar (`ONBOARDING_KEY_SAFE_ID`, appcore
 *  `packages/core/src/db/onboarding-form-repository.ts`, read at 759436a on 2026-09-26). Both
 *  segments of a source must match it, or appcore 422s the create. */
const ONBOARDING_ID_SEGMENT = /^[A-Za-z0-9._-]{1,128}$/;
const ONBOARDING_ID_SEGMENT_PAIR = /^[A-Za-z0-9._-]{1,128}#[A-Za-z0-9._-]{1,128}$/;

/**
 * `<firmWorkspaceId>#<clientId>`, or `undefined` when either part is not an onboarding id.
 *
 * The prefill stamps a source only when it can name BOTH halves from firm-side data: the firm's
 * own workspace, and the `advisor-client` id the intake writer stamped on the submission. With
 * no client id (an intake from before steward-app#1763, or one whose address the writer could
 * not tie to a record) it stamps NEITHER stamp: the copy requires both, and a subject alone
 * would only be a row nobody can copy to. Refusing here rather than stamping a guess is what
 * keeps a wrong value from reaching a createOnly field that nothing can correct later.
 */
export function onboardingSourceFor(firmWorkspaceId: unknown, clientId: unknown): string | undefined {
  if (typeof firmWorkspaceId !== 'string' || typeof clientId !== 'string') return undefined;
  if (!ONBOARDING_ID_SEGMENT.test(firmWorkspaceId) || !ONBOARDING_ID_SEGMENT.test(clientId)) return undefined;
  return `${firmWorkspaceId}#${clientId}`;
}

export interface PrefillRowIntent {
  readonly typeCode: string;
  readonly instanceId: string;
  readonly attributes: Readonly<Record<string, unknown>>;
}

export interface PrefillOptions {
  /** Identifies this sponsored-setup run. Combined with a row's `kind` and `index` into
   *  the seed passed to {@link PrefillOptions.rowId}, so the SAME setup always derives the
   *  SAME ids — which is what makes the background Lambda's create-if-absent write
   *  idempotent across redeliveries. */
  readonly setupId: string;
  /** ISO 8601 timestamp of the intake submission. Its first 10 characters (`YYYY-MM-DD`)
   *  become the `date`/`currentBalanceAsOf` stamped on every row this plan writes, since
   *  the intake itself carries no per-row date for a typed balance. */
  readonly submittedAt: string;
  /**
   * The moment setup runs (ISO 8601). Two readers, and they take the date differently:
   *  - the trusted contact reads its first 10 characters: a child counts as an adult as of this
   *    date, the same clock every other reader of the choice uses (today), so a child who turns 18
   *    between intake and setup is not shown to the firm and then dropped here (#1954);
   *  - the assets' stored latest-valuation triple (steward-app#2321) reads the UTC calendar day of
   *    the parsed moment, as the day `latestValuationRow` is evaluated at; an unparseable value
   *    plans no triple.
   * Passed in rather than read so the planner stays clock-free. Defaults to the submission
   * moment when absent.
   */
  readonly asOf?: string;
  /**
   * Turns one row's seed string into its appcore instance id. MUST return `pf` followed by
   * exactly 30 lowercase hex characters — `planPrefill` throws otherwise (see the module
   * header). The design memo's scheme (§6) is `pf<sha256(seed)[0:30]>`, and both real
   * callers (the façade and the background Lambda, both Node) inject exactly that:
   * `(seed) => 'pf' + createHash('sha256').update(seed).digest('hex').slice(0, 30)`.
   * Tests inject the same thing from `node:crypto`, which test files may import freely —
   * they are never bundled into the SPA.
   */
  readonly rowId: (seed: string) => string;
  /**
   * The firm's `onboarding-details` attributes for this client (crossmarc/steward-app#1767),
   * or absent. Read here for names, dates of birth, the legal and mailing address, children
   * and the trusted contact only; see {@link planPrefill}'s persons block for which source
   * wins where both answer.
   */
  readonly onboarding?: Readonly<Record<string, unknown>>;
  /**
   * `<firmWorkspaceId>#<clientId>` from {@link onboardingSourceFor}, or absent. When present,
   * the self row is stamped `onboardingSubject: 'client'` and the spouse row `'spouse'`, each
   * with this source (crossmarc/appcore#739). Children are never stamped: the onboarding form
   * holds no child tax id. Absent, no row carries either stamp.
   */
  readonly onboardingSource?: string;
}

/**
 * True when a Plaid snapshot exists and names at least one account this pack can honestly
 * represent as an asset — see the module header's "snapshot/typed-account rule". This is
 * the ONE place that decides it.
 *
 * Checked against `snapshotAccountShape` (below), not merely `snapshot.accounts.length`
 * (code review of this PR, 2026-09-16): a snapshot whose ONLY accounts are `credit`/`loan`
 * has ZERO classifiable rows to replace the typed ones with, so gating on "any account at
 * all" would suppress every typed cash/investment row and emit nothing in their place —
 * total data loss for a family who separately linked, say, only a credit card. Gating on
 * "any account we can actually turn into an asset" keeps the all-or-nothing rule (see the
 * header) while closing that gap: with no classifiable snapshot account, the typed rows
 * are left alone, which is exactly what would happen anyway once the unclassifiable ones
 * are filtered out.
 */
function snapshotSuppressesTypedAccounts(snapshot: IntakeDraftSnapshot | undefined): boolean {
  return !!snapshot && snapshot.accounts.some(account => snapshotAccountShape(account) !== undefined);
}

function isNonEmpty(text: string | undefined): text is string {
  return !!text && text.trim() !== '';
}

function composeNotes(lines: ReadonlyArray<string | undefined | false>): string | undefined {
  const kept = lines.filter((line): line is string => isNonEmpty(line as string | undefined));
  return kept.length ? kept.join('\n') : undefined;
}

function dateOnly(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * The intake with every section empty: what {@link planPrefill} plans from when a client has
 * onboarding details but never submitted an intake (crossmarc/steward-app#1767). It emits no
 * row of its own. `intakePrefill.test.ts` parses it through `IntakeSubmissionShape`, so it
 * cannot drift from the schema.
 */
export const EMPTY_PREFILL_INTAKE: IntakeSubmission = Object.freeze({
  gettingStarted: {},
  aboutYou: {},
  spousePartner: { hasSpousePartner: false, children: [] },
  incomeAndGoals: { incomeSources: [] },
  cashAccounts: [],
  investmentAccounts: { accounts: [] },
  realEstate: [],
  businessInterests: [],
  otherAssetsAndDebt: { otherAssets: [], debts: [] },
  insuranceAndEstate: { policies: [], estateDocuments: [] },
  currentTeam: { otherAdvisors: [] },
  investmentProfile: { assetJobs: [] },
  anythingElse: {},
}) as IntakeSubmission;

/** A trimmed, non-empty string answer from the onboarding bag, or undefined. */
function answer(bag: Readonly<Record<string, unknown>> | undefined, key: string): string | undefined {
  const value = bag?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

/** A `YYYY-MM-DD` calendar date that is real, or undefined. appcore's `date` field accepts
 *  anything `Date.parse` does; this is stricter, so a free-text date never reaches it. */
function calendarDate(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const v = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return undefined;
  const parsed = new Date(`${v}T00:00:00Z`);
  return !isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v ? v : undefined;
}

/** One onboarding address block (`address_<prefix>Street` and friends) as two lines of text:
 *  the street, then "City, ST 12345". Undefined when every part is blank. */
function onboardingAddress(bag: Readonly<Record<string, unknown>> | undefined, prefix: string): string | undefined {
  const street = answer(bag, `address_${prefix}Street`);
  const city = answer(bag, `address_${prefix}City`);
  const stateZip = [answer(bag, `address_${prefix}State`), answer(bag, `address_${prefix}PostalCode`)]
    .filter(Boolean).join(' ');
  const second = [city, stateZip].filter(Boolean).join(', ');
  const text = [street, second].filter(Boolean).join('\n');
  return text === '' ? undefined : text;
}

/** Plaid's depository `subtype` (trimmed and lower-cased here) as one of
 *  {@link PREFILL_ACCOUNT_TYPES} (crossmarc/steward-app#2225). The account-type list has no CD,
 *  Money Market, HSA or Cash Management entry, and adding one is a product change, so those
 *  land on `Other`, the same answer {@link cashAccountType} already gives the intake's own
 *  `cd` and `money-market` codes. "saving" is a substring match (banks vary: "high yield
 *  savings"). `Checking` is only `checking`, `limited purpose checking` and a missing or empty
 *  subtype; every other non-empty subtype (cd, money market, hsa, cash management, and also
 *  prepaid, paypal, ebt or one Plaid adds later) is `Other` rather than a guess at Checking. */
function depositoryAccountType(subtype: string): typeof PREFILL_ACCOUNT_TYPES[number] {
  const s = subtype.trim().toLowerCase();
  if (s === '' || s === 'checking' || s === 'limited purpose checking') return 'Checking';
  if (s.includes('saving')) return 'Savings';
  return 'Other';
}

/** Plaid's account `type`/`subtype` (`intakeSnapshot.ts`'s own docs) mapped onto this
 *  pack's `accountType` and `assetClass`. Returns `undefined` for `credit`, `loan` and
 *  any other type this pack cannot honestly represent as an asset — a card or loan
 *  balance is not net worth, so it is simply not prefilled (see the module header's PR
 *  split note: those belong to a liability-aware pass this v1 does not attempt). */
function snapshotAccountShape(account: IntakeSnapshotAccount): { accountType: string; assetClass: string } | undefined {
  const subtype = (account.subtype ?? '').toLowerCase();
  if (account.type === 'depository') {
    return { accountType: depositoryAccountType(subtype), assetClass: 'cash' };
  }
  if (account.type === 'investment' || account.type === 'brokerage') {
    const isRetirement = subtype.includes('401k') || subtype.includes('ira');
    return { accountType: isRetirement ? 'Retirement' : 'Brokerage', assetClass: PREFILL_LUMP_INVESTMENT_ASSET_CLASS };
  }
  return undefined;
}

/**
 * Plans the conservative prefill for one sponsored client setup. Pure: no I/O, no clock
 * read (the date comes from `options.submittedAt`), no randomness. The SAME `intake`,
 * `snapshot` and `options` always produce a byte-identical, frozen list — see the module
 * header and `intakePrefill.test.ts`'s determinism assertion.
 *
 * Row order is fixed and load-bearing (design memo §6): accounts, then assets, then
 * valuations, then liabilities — so a link target (`accountId`, `linkedAssetId`) is
 * always created before the row that points at it — then the onboarding trusted contact
 * (before the self row whose `trustedContactId` names it), then persons, then contacts.
 */
export function planPrefill(
  submittedIntake: IntakeSubmission | undefined,
  snapshot: IntakeDraftSnapshot | undefined,
  options: PrefillOptions,
): readonly PrefillRowIntent[] {
  // No intake (a client known only from onboarding, #1767): plan from an empty one, so every
  // intake-driven block below emits nothing and the persons block reads onboarding alone.
  const intake = submittedIntake ?? EMPTY_PREFILL_INTAKE;
  const { setupId } = options;
  const submittedDate = dateOnly(options.submittedAt);
  // The day `latestValuationAttributes` evaluates the shared rule at: the UTC day of the moment
  // setup runs (`asOf`, else the submission). Setup runs at `asOf` and the rows are written after
  // it, so the write day is never earlier than this one; a valuation dated on or before it is a
  // candidate on the write day as well. Not a claim about every possible caller of this pure
  // planner: one that passes an `asOf` later than its write would stamp a triple early. An
  // unparseable moment gives no day and so no triple.
  const planDayMoment = new Date(options.asOf ?? options.submittedAt);
  const planDay = Number.isNaN(planDayMoment.getTime()) ? undefined : utcToday(planDayMoment);
  const id = (kind: string, index: number) => deriveRowId(options.rowId, setupId, kind, index);
  // Both stamps or neither (see `onboardingSourceFor`). A caller-supplied source is re-checked
  // here, so a malformed one stamps nothing rather than a value appcore would 422 at create.
  const source = options.onboardingSource !== undefined
    && ONBOARDING_ID_SEGMENT_PAIR.test(options.onboardingSource) ? options.onboardingSource : undefined;
  const stamps = (subject: 'client' | 'spouse') =>
    source ? { [ONBOARDING_SUBJECT_KEY]: subject, [ONBOARDING_SOURCE_KEY]: source } : {};

  const accounts: PrefillRowIntent[] = [];
  const assets: PrefillRowIntent[] = [];
  const valuations: PrefillRowIntent[] = [];
  const liabilities: PrefillRowIntent[] = [];
  const trustedContacts: PrefillRowIntent[] = [];
  const persons: PrefillRowIntent[] = [];
  const contacts: PrefillRowIntent[] = [];

  /**
   * The asset's stored latest-valuation triple (steward-app#2321), from the valuation rows THIS plan
   * emits for it: `latestValuationRow` (the shared rule) over `valuationRows`, evaluated at
   * `planDay`. Nothing else is read, so an asset born here has exactly these rows and the triple
   * names the one the rule picks among them.
   *
   * It is written on the asset row at create because the actor that writes this plan holds create
   * and read only (`CLIENT_PREFILL_SCOPE`) and cannot update the asset after its valuation exists.
   * `{}` when there is no candidate (no valuation planned, or one dated after `planDay`) or the
   * plan day is unknown: a missing triple is a state every reader of the triple must already
   * handle, whereas a triple for a row that is not yet the latest is not.
   *
   * Known limits, each pinned in `services/client-prefill/src/__tests__/prefill.test.ts`:
   *  - The writer creates the asset before its valuation. If the valuation create then fails, the
   *    asset keeps a triple naming a row that does not exist. Read 2026-10-12: the record is
   *    redelivered after the queue's 900 s visibility timeout (`CLIENT_PREFILL_TIMEOUT_SECONDS` 300
   *    x 3, `deploy/lib/client-prefill-contract.ts:43`), is received at most 3 times (`:46`), then
   *    dead-letters; a DLQ re-drive is blocked by the queue policy (`deploy/lib/api-stack.ts:817`).
   *    A dead-lettered message leaves the triple as it is. The nightly latest-valuation reconcile
   *    (`services/snapshot-calculator/src/reconcile.ts`, whose docblock describes repairing a triple
   *    that names a deleted valuation) runs after the snapshot for each workspace the EOD
   *    enumerator returns (active workspaces carrying the steward-app pack,
   *    `services/workspace-enumerator/src/index.ts:29,48`), and a sponsored workspace is created
   *    `status: 'active'` (appcore `sponsored-setup-repository.ts:608`, read at appcore 6b02f97),
   *    so it is expected to be in that set; not run against such a row, and the pack list of a
   *    sponsored workspace is caller-supplied (`:523`), not checked here.
   *  - The writer is create-only, so on a retry an asset that already exists keeps whatever it has,
   *    with or without a triple (it is counted as differing in the run's log line, nothing more).
   */
  function latestValuationAttributes(valuationRows: readonly PrefillRowIntent[]): Record<string, unknown> {
    if (planDay === undefined) return {};
    const best = latestValuationRow(valuationRows, planDay, row => ({ id: row.instanceId, date: row.attributes['date'] }));
    if (best === undefined) return {};
    return {
      latestValuationValue: best.attributes['marketValue'],
      latestValuationDate: best.attributes['date'],
      latestValuationId: best.instanceId,
    };
  }

  /** Adds an account + asset pair (and a valuation when `value` parses), all linked, and
   *  returns the asset's id so a caller (real estate) can attach a liability to it. */
  function addAccountAssetValuation(kind: string, index: number, opts: {
    name: string;
    institution: string;
    accountType: string;
    assetClass: string;
    currency: string;
    value: number | null;
    date: string;
    valuationSource: string;
    lastFourHint?: string;
    accountNotes?: string;
    /** The snapshot's own account id; set only for a row derived from a snapshot account. */
    intakeAccountId?: string;
  }): string {
    const accountId = id(`${kind}-account`, index);
    accounts.push({
      typeCode: 'account',
      instanceId: accountId,
      attributes: Object.freeze({
        name: opts.name,
        institution: opts.institution,
        accountType: opts.accountType,
        currency: opts.currency,
        ...(opts.lastFourHint ? { lastFourHint: opts.lastFourHint } : {}),
        ...(opts.intakeAccountId !== undefined ? { intakeAccountId: opts.intakeAccountId } : {}),
        ...(isNonEmpty(opts.accountNotes) ? { notes: opts.accountNotes } : {}),
      }),
    });
    const assetId = id(`${kind}-asset`, index);
    const valuation: PrefillRowIntent | undefined = opts.value != null ? {
      typeCode: 'valuation',
      instanceId: valuationInstanceId(assetId, opts.date),
      attributes: Object.freeze({
        assetId,
        date: opts.date,
        marketValue: opts.value,
        currency: opts.currency,
        source: opts.valuationSource,
      }),
    } : undefined;
    assets.push({
      typeCode: 'asset',
      instanceId: assetId,
      attributes: Object.freeze({
        accountId,
        name: opts.name,
        assetClass: opts.assetClass,
        currency: opts.currency,
        ...latestValuationAttributes(valuation ? [valuation] : []),
      }),
    });
    if (valuation) valuations.push(valuation);
    return assetId;
  }

  // ── Accounts + assets + valuations: cash and investment, unless a snapshot suppresses them ──
  if (!snapshotSuppressesTypedAccounts(snapshot)) {
    // A row holding nothing but its id (crossmarc/steward-app#2185) is an empty hand-entry row, and
    // is not an account: nothing is created from it. The index still counts it, so the ids of the
    // others do not move.
    intake.cashAccounts.forEach((row, index) => {
      if (isEmptyValue(row)) return;
      const typeLabel = row.accountType ? CASH_TYPE_LABEL[row.accountType] : undefined;
      const institution = isNonEmpty(row.institution) ? row.institution.trim() : UNNAMED_INSTITUTION_FALLBACK;
      const name = isNonEmpty(row.institution)
        ? `${row.institution.trim()} ${typeLabel ?? 'Cash'}`
        : `${typeLabel ?? 'Cash'} account`;
      addAccountAssetValuation('cash', index, {
        name,
        institution,
        accountType: row.accountType ? cashAccountType(row.accountType) : 'Other',
        assetClass: 'cash',
        currency: PREFILL_CURRENCY,
        value: parseStrictMoney(row.balance),
        date: submittedDate,
        valuationSource: PREFILL_VALUATION_SOURCE,
      });
    });

    intake.investmentAccounts.accounts.forEach((row, index) => {
      if (isEmptyValue(row)) return;
      const typeLabel = row.accountType ? INVESTMENT_TYPE_LABEL[row.accountType] : undefined;
      const institution = isNonEmpty(row.institution) ? row.institution.trim() : UNNAMED_INSTITUTION_FALLBACK;
      const name = isNonEmpty(row.institution)
        ? `${row.institution.trim()} ${typeLabel ?? 'Investment account'}`
        : (typeLabel ?? 'Investment account');
      addAccountAssetValuation('investment', index, {
        name,
        institution,
        accountType: row.accountType ? investmentAccountType(row.accountType) : 'Other',
        assetClass: PREFILL_LUMP_INVESTMENT_ASSET_CLASS,
        currency: PREFILL_CURRENCY,
        value: parseStrictMoney(row.balance),
        date: submittedDate,
        valuationSource: PREFILL_VALUATION_SOURCE,
        accountNotes: isNonEmpty(row.owner) ? `Owner (from your intake): ${row.owner.trim()}` : undefined,
      });
    });
  }

  // ── Accounts + assets + valuations: the Plaid snapshot, when it suppresses the above ──
  if (snapshot && snapshotSuppressesTypedAccounts(snapshot)) {
    snapshot.accounts.forEach((account, index) => {
      const shape = snapshotAccountShape(account);
      if (!shape) return; // credit/loan/unclassifiable — see snapshotAccountShape's docs.
      addAccountAssetValuation('snapshot', index, {
        name: account.name,
        institution: isNonEmpty(account.institution) ? account.institution.trim() : UNNAMED_INSTITUTION_FALLBACK,
        accountType: shape.accountType,
        assetClass: shape.assetClass,
        currency: account.currency ?? PREFILL_CURRENCY,
        value: account.balance,
        date: dateOnly(snapshot.asOf),
        valuationSource: PREFILL_SNAPSHOT_VALUATION_SOURCE,
        lastFourHint: account.mask,
        // Stamped for a later match against a connection's own account ids (steward-app#2171).
        // Plaid account ids are Item-scoped (`intakeSnapshot.ts`), and the snapshot reader removes
        // its Item at the end of every exchange (`services/intake-reader/src/plaidSnapshot.ts`,
        // "THE ONE RULE": no path out of `exchangeAndPull` leaves an Item alive). So this id can
        // equal a later connection's account id only when that connection was made from an Item
        // that was kept.
        intakeAccountId: account.accountId,
      });
    });
  }

  // ── Assets (+ valuations, + a linked liability): real estate ──
  const realEstateAssetIds: string[] = [];
  intake.realEstate.forEach((row, index) => {
    const name = isNonEmpty(row.description)
      ? row.description.trim()
      : (row.propertyType && Object.hasOwn(PROPERTY_TYPE_NAME, row.propertyType) ? PROPERTY_TYPE_NAME[row.propertyType] : undefined) ?? `Real estate ${index + 1}`;
    // Only what the row's kind asks (`propertyAsks`, shared with the wizard): a stored answer the
    // kind hides is not written to the client's record.
    const asks = (field: string) => propertyAsks(row.propertyType, field);
    const assetId = id('real-estate-asset', index);
    realEstateAssetIds[index] = assetId;
    const value = parseStrictMoney(row.estimatedValue);
    const valuation: PrefillRowIntent | undefined = value != null ? {
      typeCode: 'valuation',
      instanceId: valuationInstanceId(assetId, submittedDate),
      attributes: Object.freeze({
        assetId,
        date: submittedDate,
        marketValue: value,
        currency: PREFILL_CURRENCY,
        source: PREFILL_VALUATION_SOURCE,
      }),
    } : undefined;
    assets.push({
      typeCode: 'asset',
      instanceId: assetId,
      attributes: Object.freeze({
        name,
        assetClass: 'real estate',
        currency: PREFILL_CURRENCY,
        // 'stated' — never 'avm' — so this row never enters the RentCast refresher
        // (design memo §3: "'avm' would bring in the RentCast refresher").
        valuationMode: 'stated',
        // The type-dependent answers (rent, purchase, plan) have no field on the asset, so they
        // ride along as notes, labelled as the family's own words like the business notes below.
        notes: composeNotes([
          asks('landWhatItIs') && isNonEmpty(row.landWhatItIs) && `What it is (from your intake): ${row.landWhatItIs.trim()}`,
          asks('purchasePrice') && isNonEmpty(row.purchasePrice) && `Purchase price (from your intake): ${row.purchasePrice.trim()}`,
          asks('purchaseYear') && isNonEmpty(row.purchaseYear) && `Year bought (from your intake): ${row.purchaseYear.trim()}`,
          asks('monthlyRent') && isNonEmpty(row.monthlyRent) && `Monthly rent (from your intake): ${row.monthlyRent.trim()}`,
          asks('occupancy') && row.occupancy && `Currently (from your intake): ${intakeLabel(RENTAL_OCCUPANCY_LABELS, row.occupancy)}`,
          asks('businessRentsSpace') && row.businessRentsSpace && `Rented by your business (from your intake): ${intakeLabel(BUSINESS_RENTS_SPACE_LABELS, row.businessRentsSpace)}`,
          asks('vacationRentalIncome') && row.vacationRentalIncome && `Rental income (from your intake): ${intakeLabel(VACATION_RENTAL_INCOME_LABELS, row.vacationRentalIncome)}`,
          asks('annualRentalIncome') && isNonEmpty(row.annualRentalIncome) && `Annual rental income (from your intake): ${row.annualRentalIncome.trim()}`,
          asks('longTermPlan') && row.longTermPlan && `Long-term plan (from your intake): ${intakeLabel(PROPERTY_PLAN_LABELS, row.longTermPlan)}`,
          asks('landPlan') && isNonEmpty(row.landPlan) && `Plan for the land (from your intake): ${row.landPlan.trim()}`,
          asks('improvements') && isNonEmpty(row.improvements) && `Improvements since purchase (from your intake): ${row.improvements.trim()}`,
        ]),
        ...latestValuationAttributes(valuation ? [valuation] : []),
      }),
    });
    if (valuation) valuations.push(valuation);
  });

  // ── Assets (no valuation in v1): business interests ──
  intake.businessInterests.forEach((row, index) => {
    const name = isNonEmpty(row.name) ? row.name.trim() : `Business interest ${index + 1}`;
    assets.push({
      typeCode: 'asset',
      instanceId: id('business-asset', index),
      attributes: Object.freeze({
        name,
        assetClass: 'business',
        currency: PREFILL_CURRENCY,
        // No valuation row is written for a business in v1 (design memo §3), so the raw
        // figure would otherwise vanish from the record entirely. Kept here, honestly
        // labelled: the wizard's own field label ("Roughly, what your stake is worth",
        // `src/intake/sections/BusinessInterests.tsx`) confirms this is the FAMILY'S
        // SHARE, not the whole business, which is why it is never treated as a valuation.
        notes: composeNotes([
          isNonEmpty(row.estimatedValue) && `Estimated stake value (from your intake, not a valuation): ${row.estimatedValue.trim()}`,
          isNonEmpty(row.ownershipPercentage) && `Ownership: ${row.ownershipPercentage.trim()}%`,
          isNonEmpty(row.lastValuationDate) && `Last valuation date (from your intake): ${row.lastValuationDate.trim()}`,
          isNonEmpty(row.completedBy) && `Completed by (from your intake): ${row.completedBy.trim()}`,
          row.entityType && `Type of business (from your intake): ${intakeLabel(ENTITY_TYPE_LABELS, row.entityType)}`,
          row.annualRevenue && `Yearly revenue (from your intake): ${intakeLabel(REVENUE_RANGE_LABELS, row.annualRevenue)}`,
          row.employeeCount && `Employees (from your intake): ${intakeLabel(EMPLOYEE_COUNT_LABELS, row.employeeCount)}`,
          row.ownerPay && `How you are paid (from your intake): ${intakeLabel(OWNER_PAY_LABELS, row.ownerPay)}`,
          isNonEmpty(row.annualTakeHome) && `Taken out in a year (from your intake): ${row.annualTakeHome.trim()}`,
          row.otherOwners && `Other owners (from your intake): ${intakeLabel(OTHER_OWNERS_LABELS, row.otherOwners)}`,
          asksOtherOwnersDetails(row.otherOwners) && isNonEmpty(row.otherOwnersDetails) && `Other owners, described (from your intake): ${row.otherOwnersDetails.trim()}`,
          row.businessHoldings && `Owns real estate or equipment (from your intake): ${intakeLabel(BUSINESS_HOLDINGS_LABELS, row.businessHoldings)}`,
          asksBusinessHoldingsDetails(row.businessHoldings) && isNonEmpty(row.businessHoldingsDetails) && `What it owns (from your intake): ${row.businessHoldingsDetails.trim()}`,
          row.exitThinking && `Exit or succession thinking (from your intake): ${intakeLabel(EXIT_THINKING_LABELS, row.exitThinking)}`,
          row.buySellAgreement && `Buy-sell agreement (from your intake): ${intakeLabel(BUY_SELL_LABELS, row.buySellAgreement)}`,
          isNonEmpty(row.ifSomethingHappened) && `If something happened to you (from your intake): ${row.ifSomethingHappened.trim()}`,
        ]),
      }),
    });
  });

  // ── Assets (+ valuations): other assets ──
  intake.otherAssetsAndDebt.otherAssets.forEach((row, index) => {
    const name = isNonEmpty(row.description)
      ? row.description.trim()
      : (row.assetType ? OTHER_ASSET_TYPE_NAME[row.assetType] : undefined) ?? `Other asset ${index + 1}`;
    const assetId = id('other-asset', index);
    const value = parseStrictMoney(row.estimatedValue);
    const valuation: PrefillRowIntent | undefined = value != null ? {
      typeCode: 'valuation',
      instanceId: valuationInstanceId(assetId, submittedDate),
      attributes: Object.freeze({
        assetId,
        date: submittedDate,
        marketValue: value,
        currency: PREFILL_CURRENCY,
        source: PREFILL_VALUATION_SOURCE,
      }),
    } : undefined;
    assets.push({
      typeCode: 'asset',
      instanceId: assetId,
      attributes: Object.freeze({
        name,
        assetClass: row.assetType ? OTHER_ASSET_CLASS[row.assetType] : 'other',
        currency: PREFILL_CURRENCY,
        ...latestValuationAttributes(valuation ? [valuation] : []),
      }),
    });
    if (valuation) valuations.push(valuation);
  });

  // ── Liabilities: a mortgage per real-estate row whose balance parses ──
  intake.realEstate.forEach((row, index) => {
    const balance = parseStrictMoney(row.mortgageBalance);
    if (balance == null) return; // currentBalance is required; an unparseable balance skips the row.
    // A rate on a kind that does not ask for one (Land, Commercial) is not used.
    const rate = propertyAsks(row.propertyType, 'mortgageRate') ? parseStrictPercent(row.mortgageRate) : null;
    liabilities.push({
      typeCode: 'liability',
      instanceId: id('mortgage-liability', index),
      attributes: Object.freeze({
        name: 'Mortgage',
        liabilityType: 'mortgage',
        currentBalance: balance,
        currency: PREFILL_CURRENCY,
        currentBalanceAsOf: submittedDate,
        linkedAssetId: realEstateAssetIds[index],
        ...(rate != null ? { interestRate: rate } : {}),
      }),
    });
  });

  // ── Liabilities: debts whose balance parses (required, so an unparseable one is skipped) ──
  intake.otherAssetsAndDebt.debts.forEach((row, index) => {
    const balance = parseStrictMoney(row.balance);
    if (balance == null) return;
    const name = isNonEmpty(row.description)
      ? row.description.trim()
      : (row.debtType ? DEBT_TYPE_NAME[row.debtType] : undefined) ?? `Debt ${index + 1}`;
    const rate = parseStrictPercent(row.interestRate);
    liabilities.push({
      typeCode: 'liability',
      instanceId: id('debt-liability', index),
      attributes: Object.freeze({
        name,
        liabilityType: row.debtType ? debtLiabilityType(row.debtType) : 'other',
        currentBalance: balance,
        currency: PREFILL_CURRENCY,
        currentBalanceAsOf: submittedDate,
        ...(rate != null ? { interestRate: rate } : {}),
        ...(isNonEmpty(row.monthlyPayment)
          ? { notes: `Monthly payment (from your intake): ${row.monthlyPayment.trim()}` }
          : {}),
      }),
    });
  });

  // ── Persons: aboutYou, spouse, children ──
  //
  // steward-app#1564: the intake already captures first and last name separately, and the
  // prefill used to throw that split away by joining it into `legalName` alone. Now both
  // the split (`firstName`/`lastName`) AND the join (`legalName`, still the type's one
  // required field) are written, self and spouse alike — `legalName` derived here, the same
  // `` `${first} ${last}`.trim() `` this file always used, so a pre-#1564 reader that only
  // knows `legalName` sees exactly what it saw before. Emitted only when actually non-empty,
  // matching every other optional field this planner writes.
  //
  // Children get no first/last split (founder ruling, issue #1564): the intake asks for a
  // single `child.name`, never a split, and so does onboarding (`children_names`). They do
  // get a date of birth when there is a real one (#1767, below).
  //
  // crossmarc/steward-app#1767: the firm's ONBOARDING DETAILS, when there are any, are read
  // alongside the intake. The rules, each one because onboarding is the later and the legal
  // answer (its name fields are labelled "Legal first name" / "Legal last name"):
  //   - names: the onboarding first/middle/last win when either the first or the last is
  //     answered there; otherwise the intake's, exactly as before;
  //   - the spouse: `spouse_included`, when the onboarding answered it either way, decides
  //     whether there is a spouse row at all; otherwise the intake's `hasSpousePartner`;
  //   - children: `children_names`, when the onboarding holds the list at all (even empty, so
  //     a cleared section stays cleared), decides who the children are, with
  //     `children_dateOfBirths` read by index; otherwise the intake's children, each with its
  //     own date of birth when that is a real date;
  //   - dates of birth and addresses come only from onboarding (the intake asks neither for an
  //     adult). The spouse gets the client's legal address when `address_spouseSameAsClient`
  //     is true, else their own. The mailing address is written only when the onboarding says
  //     it is NOT the same as the legal one and it differs as text (#1260: never mirrored).
  // Ids are unchanged (`person` 0 and 1, `person-child` by list index), so a message planned
  // either way writes the same rows.
  const onb = options.onboarding;
  // Who the trusted contact is (#1954), re-checked against the answers as they stand at setup.
  // `options.asOf` (the setup time) is the clock: a child counts as an adult as of setup, like
  // every other reader, and this function reads no clock of its own.
  const trusted = resolveTrustedContact(onb, options.asOf ? dateOnly(options.asOf) : submittedDate);
  const trustedContactName = trusted?.subject === 'other' && trusted.name ? trusted.name : undefined;
  const trustedContactRowId = trustedContactName ? id('contact-trusted', 0) : undefined;
  if (trustedContactName) {
    const relationshipText = trusted!.relationship;
    const phone = trusted!.phone;
    const email = trusted!.email;
    // Planned BEFORE the persons that point at it (the writer creates rows in message order),
    // so `trustedContactId` never names a row that is not there yet.
    trustedContacts.push({
      typeCode: 'contact',
      instanceId: trustedContactRowId!,
      attributes: Object.freeze({
        name: trustedContactName,
        category: 'other',
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
        notes: composeNotes([
          'Trusted contact (from your onboarding)',
          relationshipText && `Relationship: ${relationshipText}`,
        ]),
      }),
    });
  }

  const legalAddress = onboardingAddress(onb, 'legal');
  const mailing = onb?.['address_mailingSameAsLegal'] === false ? onboardingAddress(onb, 'mailing') : undefined;
  const mailingAddress = mailing !== undefined && mailing !== legalAddress ? mailing : undefined;
  const spouseAddress = onb?.['address_spouseSameAsClient'] === true ? legalAddress : onboardingAddress(onb, 'spouseLegal');

  /** The name triple for `client`/`spouse`: onboarding when it answers a first or last name,
   *  else the intake person's. */
  const namesFor = (subject: 'client' | 'spouse', intakePerson: { firstName?: string; lastName?: string } | undefined) => {
    const first = answer(onb, `${subject}_firstName`);
    const last = answer(onb, `${subject}_lastName`);
    if (first || last) {
      const middle = answer(onb, `${subject}_middleName`);
      return { first, last, legal: [first, middle, last].filter(Boolean).join(' ') };
    }
    const iFirst = intakePerson && isNonEmpty(intakePerson.firstName) ? intakePerson.firstName.trim() : undefined;
    const iLast = intakePerson && isNonEmpty(intakePerson.lastName) ? intakePerson.lastName.trim() : undefined;
    return { first: iFirst, last: iLast, legal: `${iFirst ?? ''} ${iLast ?? ''}`.trim() };
  };

  const self = namesFor('client', intake.aboutYou);
  const spouseAnswered = typeof onb?.['spouse_included'] === 'boolean';
  const hasSpouse = spouseAnswered ? onb?.['spouse_included'] === true : intake.spousePartner.hasSpousePartner;
  const intakeSpouse = intake.spousePartner.hasSpousePartner ? intake.spousePartner.spouse : undefined;
  const spouse = hasSpouse ? namesFor('spouse', intakeSpouse) : undefined;
  // The family member the client chose as their trusted contact (#1954), when that member's
  // person row is one this plan will emit: the spouse's exists when there is a spouse with a
  // name, a child's when the child has a name (`resolveTrustedContact` already required that),
  // and ids are positional (`person` 1, `person-child` by list index). The target row is moved
  // ahead of the self row below, so the pointer never names a row the writer has not created.
  const memberTargetId = trusted?.subject === 'spouse' && spouse?.legal ? id('person', 1)
    : trusted?.subject === 'child' && trusted.childIndex !== undefined ? id('person-child', trusted.childIndex)
      : undefined;
  const trustedContactId = trustedContactRowId ?? memberTargetId;
  if (self.legal) {
    const dateOfBirth = calendarDate(onb?.['client_dateOfBirth']);
    persons.push({
      typeCode: 'person',
      instanceId: id('person', 0),
      attributes: Object.freeze({
        legalName: self.legal,
        ...(self.first ? { firstName: self.first } : {}),
        ...(self.last ? { lastName: self.last } : {}),
        relationship: RELATIONSHIP_SELF,
        ...(dateOfBirth ? { dateOfBirth } : {}),
        ...(legalAddress ? { legalAddress } : {}),
        ...(mailingAddress ? { mailingAddress } : {}),
        ...(trustedContactId ? { trustedContactId } : {}),
        ...stamps('client'),
      }),
    });
  }
  if (spouse?.legal) {
    const dateOfBirth = calendarDate(onb?.['spouse_dateOfBirth']);
    // The spouse's own onboarding answers (#1957), whether or not the spouse is also the trusted
    // contact: the resolver reads these same two keys for a spouse, so there is nothing to fall back to.
    const spousePhone = answer(onb, 'spouse_phone');
    const spouseEmail = answer(onb, 'spouse_email');
    persons.push({
      typeCode: 'person',
      instanceId: id('person', 1),
      attributes: Object.freeze({
        legalName: spouse.legal,
        ...(spouse.first ? { firstName: spouse.first } : {}),
        ...(spouse.last ? { lastName: spouse.last } : {}),
        relationship: RELATIONSHIP_SPOUSE,
        ...(spousePhone ? { phone: spousePhone } : {}),
        ...(spouseEmail ? { email: spouseEmail } : {}),
        ...(dateOfBirth ? { dateOfBirth } : {}),
        ...(spouseAddress ? { legalAddress: spouseAddress } : {}),
        ...stamps('spouse'),
      }),
    });
  }
  const onbChildren = onb?.['children_names'];
  const children: Array<{ name: unknown; dateOfBirth: unknown }> = Array.isArray(onbChildren)
    ? onbChildren.map((name, index) => ({
      name,
      dateOfBirth: Array.isArray(onb?.['children_dateOfBirths']) ? (onb['children_dateOfBirths'] as unknown[])[index] : undefined,
    }))
    : intake.spousePartner.children.map(child => ({ name: child.name, dateOfBirth: child.dateOfBirth }));
  children.forEach((child, index) => {
    if (typeof child.name !== 'string' || !isNonEmpty(child.name)) return;
    const dateOfBirth = calendarDate(child.dateOfBirth);
    // Only the child the client chose as their trusted contact, and only while that choice still
    // resolves (an adult, the same name): the phone and email the form asked for, which the
    // resolver returns. A minor or a stale choice resolves to `other`, so no child gets any.
    const trustedChild = trusted?.subject === 'child' && trusted.childIndex === index ? trusted : undefined;
    persons.push({
      typeCode: 'person',
      instanceId: id('person-child', index),
      attributes: Object.freeze({
        legalName: child.name.trim(),
        relationship: RELATIONSHIP_CHILD,
        ...(trustedChild?.phone ? { phone: trustedChild.phone } : {}),
        ...(trustedChild?.email ? { email: trustedChild.email } : {}),
        ...(dateOfBirth ? { dateOfBirth } : {}),
      }),
    });
  });

  // ── Contacts: CPA, attorney, other advisors — never relationship / relationshipQuality ──
  const cpa = intake.currentTeam.cpa;
  if (cpa && isNonEmpty(cpa.name)) {
    contacts.push({
      typeCode: 'contact',
      instanceId: id('contact-cpa', 0),
      attributes: Object.freeze({
        name: cpa.name.trim(),
        category: CPA_ATTORNEY_CATEGORY.cpa,
        ...(isNonEmpty(cpa.firm) ? { organization: cpa.firm.trim() } : {}),
      }),
    });
  }
  const attorney = intake.currentTeam.attorney;
  if (attorney && isNonEmpty(attorney.name)) {
    contacts.push({
      typeCode: 'contact',
      instanceId: id('contact-attorney', 0),
      attributes: Object.freeze({
        name: attorney.name.trim(),
        category: CPA_ATTORNEY_CATEGORY.attorney,
        ...(isNonEmpty(attorney.firm) ? { organization: attorney.firm.trim() } : {}),
      }),
    });
  }
  intake.currentTeam.otherAdvisors.forEach((advisor, index) => {
    if (!isNonEmpty(advisor.name)) return;
    contacts.push({
      typeCode: 'contact',
      instanceId: id('contact-advisor', index),
      attributes: Object.freeze({
        name: advisor.name.trim(),
        category: guessAdvisorCategory(advisor.role),
        ...(isNonEmpty(advisor.firm) ? { organization: advisor.firm.trim() } : {}),
        ...(isNonEmpty(advisor.role) ? { notes: `Role (from your intake): ${advisor.role.trim()}` } : {}),
      }),
    });
  });

  if (memberTargetId) {
    const target = persons.findIndex((p) => p.instanceId === memberTargetId);
    const selfAt = persons.findIndex((p) => p.instanceId === id('person', 0));
    if (target > selfAt && selfAt >= 0) persons.splice(selfAt, 0, ...persons.splice(target, 1));
  }
  return Object.freeze([...accounts, ...assets, ...valuations, ...liabilities, ...trustedContacts, ...persons, ...contacts]);
}
